import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useLanguage } from "../LanguageContext";
import ArticleCard from "../features/ArticleCard";
import ArticleCardSkeleton from "../features/ArticleCardSkeleton";
import Pagination from "../features/Pagination";
import { wpApi, WPPost } from "../../services/wordpress";

// Transform WordPress post to match our ArticleMetadata interface
interface ArticleMetadata {
  id: string;
  title: string;
  excerpt: string;
  publishDate: string;
}

function transformPost(post: WPPost): ArticleMetadata {
  return {
    id: post.slug,
    title: wpApi.stripHtml(post.title.rendered),
    excerpt: post.excerpt.rendered 
      ? wpApi.stripHtml(post.excerpt.rendered)
      : wpApi.createExcerpt(post.content.rendered, 150),
    publishDate: post.date
  };
}

export default function NewsMarketInsightsWordPress() {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const [articles, setArticles] = useState<ArticleMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const articlesPerPage = 6;

  // Fetch articles from WordPress with pagination
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching page', currentPage, 'from WordPress API...');
        console.log('API URL:', import.meta.env.VITE_WP_API_URL || 'http://localhost:8080/wp-json/wp/v2');
        
        // Get posts from WordPress with pagination
        const { posts, totalCount } = await wpApi.getPostsPaginated({
          per_page: articlesPerPage,
          page: currentPage,
          orderby: 'date',
          order: 'desc'
          // Removed _embed for faster loading
        });
        
        console.log('WordPress posts received:', posts.length, 'posts');
        console.log('Total posts available:', totalCount);
        
        // Transform posts to our format
        const transformedArticles = posts.map(transformPost);
        setArticles(transformedArticles);
        
        // Calculate total pages
        const pages = Math.ceil(totalCount / articlesPerPage);
        setTotalPages(pages);
        console.log('Total pages:', pages);
      } catch (err) {
        console.error('Error fetching WordPress articles:', err);
        setError(err instanceof Error ? err.message : 'Failed to load articles');
        
        // Fallback to empty array
        setArticles([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [currentPage]); // Re-fetch when page changes

  // Articles are already paginated from the API
  const currentArticles = articles;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of articles section
    const articlesSection = document.getElementById('articles-section');
    if (articlesSection) {
      const headerOffset = 100;
      const elementPosition = articlesSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden pt-26">
      <main className="relative">
        {/* Hero Section */}
        <section className="pt-16 pb-20 relative overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Full Width Text Content - No Image */}
            <motion.div
              className="text-center space-y-8 max-w-5xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="text-5xl lg:text-7xl leading-tight text-foreground">
                  {t('marketInsights.heroTitle')}{" "}
                  <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-cyan-600 bg-clip-text text-transparent">
                    {t('marketInsights.heroHighlight')}
                  </span>
                </h1>

                <motion.p
                  className="text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {t('marketInsights.heroSubtitle')}
                </motion.p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Articles Section */}
        <section id="articles-section" className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {t('marketInsights.articlesTitle')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t('marketInsights.articlesSubtitle').split('\n').map((line, i, arr) => (
                  <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </p>
            </motion.div>

            {/* Loading State with Skeletons */}
            {loading && (
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {[...Array(articlesPerPage)].map((_, index) => (
                  <ArticleCardSkeleton key={`skeleton-${index}`} />
                ))}
              </motion.div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-8">
                <p className="font-bold">Error loading articles</p>
                <p className="text-sm">{error}</p>
                <p className="text-xs mt-2">
                  Make sure WordPress is running at {import.meta.env.VITE_WP_API_URL || 'http://localhost:8080'}
                </p>
              </div>
            )}

            {/* No Articles State */}
            {!loading && !error && articles.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
                <p className="font-bold">No articles found</p>
                <p className="text-sm">
                  Create some posts in WordPress Admin first, or run the migration script to import existing MDX articles.
                </p>
              </div>
            )}

            {/* Articles List - One per row */}
            {!loading && articles.length > 0 && (
              <>
                <motion.div
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  {currentArticles.map((article, index) => (
                    <motion.div
                      key={article.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <ArticleCard article={article} index={index} />
                    </motion.div>
                  ))}
                </motion.div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <motion.div
                    className="mt-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </motion.div>
                )}
              </>
            )}

          </div>
        </section>
      </main>
    </div>
  );
}